import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function FocusGroupAnalytics({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedQuestions, setExpandedQuestions] = useState({});

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get('/api/focus-group/analytics', { withCredentials: true });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sage-400 border-t-transparent"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Failed to load analytics data.
      </div>
    );
  }

  const { questionAggregates, engagementScores, progressSummary, classCodes } = data;
  const group = activeTab;
  const engagement = engagementScores[group] || { avg: 0, participants: [] };
  const questions = questionAggregates[group] || {};
  const progress = progressSummary[group] || {};

  const engagementColor = (score) => score > 70 ? 'bg-green-500' : score > 40 ? 'bg-amber-500' : 'bg-red-500';
  const engagementTextColor = (score) => score > 70 ? 'text-green-600' : score > 40 ? 'text-amber-600' : 'text-red-600';

  // Score distribution buckets
  const buckets = [0, 0, 0, 0]; // 0-25, 25-50, 50-75, 75-100
  engagement.participants.forEach(p => {
    if (p.composite >= 75) buckets[3]++;
    else if (p.composite >= 50) buckets[2]++;
    else if (p.composite >= 25) buckets[1]++;
    else buckets[0]++;
  });
  const totalParticipants = engagement.participants.length;

  // Component averages
  const componentAvgs = { sessionCompletion: 0, daysActive: 0, writingEffort: 0, sessionDepth: 0, missionCompletion: 0 };
  if (totalParticipants > 0) {
    engagement.participants.forEach(p => {
      Object.keys(componentAvgs).forEach(k => { componentAvgs[k] += p.components[k]; });
    });
    Object.keys(componentAvgs).forEach(k => { componentAvgs[k] = Math.round(componentAvgs[k] / totalParticipants); });
  }

  const componentLabels = {
    sessionCompletion: 'Session Completion',
    daysActive: 'Days Active',
    writingEffort: 'Writing Effort',
    sessionDepth: 'Session Depth',
    missionCompletion: 'Mission Completion',
  };
  const componentColors = {
    sessionCompletion: 'bg-blue-500',
    daysActive: 'bg-green-500',
    writingEffort: 'bg-purple-500',
    sessionDepth: 'bg-amber-500',
    missionCompletion: 'bg-rose-500',
  };

  // Group questions by submission type
  const surveyTypeLabels = {
    first_impressions: 'First Impressions',
    mid_study: 'Mid-Study Check-In',
    evaluation: 'Final Evaluation',
  };
  const surveyTypeOrder = ['first_impressions', 'mid_study', 'evaluation'];

  const questionsBySubmissionType = {};
  Object.entries(questions).forEach(([qId, q]) => {
    const st = q.submissionType || 'other';
    if (!questionsBySubmissionType[st]) questionsBySubmissionType[st] = [];
    questionsBySubmissionType[st].push([qId, q]);
  });

  const ratingColors = { 1: 'bg-red-400', 2: 'bg-orange-400', 3: 'bg-amber-400', 4: 'bg-lime-400', 5: 'bg-green-400' };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link to="/focus-groups/feedback" className="text-forest-800 hover:text-forest-700">
                ← Feedback
              </Link>
              <h1 className="text-2xl font-bold text-forest-800">Focus Group Analytics</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Class Tabs */}
        <div className="flex space-x-1 bg-white rounded-lg shadow p-1 mb-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'all' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            All Classes
          </button>
          {classCodes.map(cc => (
            <button
              key={cc}
              onClick={() => setActiveTab(cc)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === cc ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {cc}
            </button>
          ))}
        </div>

        {/* Section 1: Engagement Overview */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Engagement Overview</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Big Score */}
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className={`text-5xl font-bold ${engagementTextColor(engagement.avg)}`}>
                {engagement.avg}
              </div>
              <div className="text-sm text-gray-500 mt-1">Average Engagement Score</div>
              <div className="text-xs text-gray-400 mt-1">{totalParticipants} participant{totalParticipants !== 1 ? 's' : ''}</div>

              {/* Progress summary */}
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-lg font-semibold text-blue-600">{progress.avgSessions || 0}</div>
                  <div className="text-xs text-gray-400">Avg Sessions</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-green-600">{progress.avgDaysActive || 0}</div>
                  <div className="text-xs text-gray-400">Avg Days Active</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-purple-600">{progress.avgWordCount || 0}</div>
                  <div className="text-xs text-gray-400">Avg Words</div>
                </div>
              </div>
            </div>

            {/* Score Distribution */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-3">Score Distribution</h3>
              <div className="flex h-8 rounded-full overflow-hidden bg-gray-100">
                {totalParticipants > 0 && (
                  <>
                    {buckets[0] > 0 && (
                      <div
                        className="bg-red-400 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${(buckets[0] / totalParticipants) * 100}%` }}
                        title={`0-24: ${buckets[0]}`}
                      >{buckets[0]}</div>
                    )}
                    {buckets[1] > 0 && (
                      <div
                        className="bg-amber-400 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${(buckets[1] / totalParticipants) * 100}%` }}
                        title={`25-49: ${buckets[1]}`}
                      >{buckets[1]}</div>
                    )}
                    {buckets[2] > 0 && (
                      <div
                        className="bg-lime-400 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${(buckets[2] / totalParticipants) * 100}%` }}
                        title={`50-74: ${buckets[2]}`}
                      >{buckets[2]}</div>
                    )}
                    {buckets[3] > 0 && (
                      <div
                        className="bg-green-500 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${(buckets[3] / totalParticipants) * 100}%` }}
                        title={`75-100: ${buckets[3]}`}
                      >{buckets[3]}</div>
                    )}
                  </>
                )}
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0-24</span><span>25-49</span><span>50-74</span><span>75-100</span>
              </div>

              {/* Component Breakdown */}
              <div className="mt-5 space-y-2">
                {Object.entries(componentLabels).map(([key, label]) => (
                  <div key={key}>
                    <div className="flex justify-between text-xs text-gray-600 mb-0.5">
                      <span>{label}</span>
                      <span className="font-medium">{componentAvgs[key]}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${componentColors[key]} rounded-full transition-all`}
                        style={{ width: `${componentAvgs[key]}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Per-Class Comparison (only on All tab) */}
            {activeTab === 'all' && classCodes.length > 1 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-600 mb-3">Class Comparison</h3>
                <div className="space-y-3">
                  {classCodes.map(cc => {
                    const classEng = engagementScores[cc] || { avg: 0, participants: [] };
                    return (
                      <div key={cc}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{cc}</span>
                          <span className={`font-bold ${engagementTextColor(classEng.avg)}`}>{classEng.avg}</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${engagementColor(classEng.avg)} rounded-full transition-all`}
                            style={{ width: `${classEng.avg}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-400">{classEng.participants.length} participants</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Section 2: Survey Responses - grouped by survey type */}
        {surveyTypeOrder.filter(st => questionsBySubmissionType[st]).map(st => {
          const stQuestions = questionsBySubmissionType[st];
          const ratings = stQuestions.filter(([, q]) => q.type === 'rating');
          const mcs = stQuestions.filter(([, q]) => q.type === 'multipleChoice');
          const texts = stQuestions.filter(([, q]) => q.type === 'text');

          return (
            <section key={st} className="mb-10">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">{surveyTypeLabels[st] || st}</h2>

              {/* Ratings */}
              {ratings.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {ratings.map(([qId, q]) => (
                    <div key={qId} className="bg-white rounded-lg shadow p-4">
                      <div className="text-sm font-medium text-gray-700 mb-2">{q.questionText}</div>
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-3xl font-bold text-indigo-600">{q.avg}</span>
                        <span className="text-amber-400 text-lg">
                          {'★'.repeat(Math.round(q.avg))}{'☆'.repeat(Math.max(0, 5 - Math.round(q.avg)))}
                        </span>
                        <span className="text-xs text-gray-400">({q.count} responses)</span>
                      </div>
                      <div className="flex h-4 rounded-full overflow-hidden bg-gray-100">
                        {[1, 2, 3, 4, 5].map(n => {
                          const count = q.distribution[n] || 0;
                          if (count === 0) return null;
                          return (
                            <div
                              key={n}
                              className={`${ratingColors[n]} flex items-center justify-center text-xs text-white`}
                              style={{ width: `${(count / q.count) * 100}%` }}
                              title={`${n}: ${count}`}
                            />
                          );
                        })}
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Multiple Choice */}
              {mcs.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {mcs.map(([qId, q]) => {
                    const sorted = Object.entries(q.frequencies || {}).sort((a, b) => b[1] - a[1]);
                    const maxCount = sorted.length > 0 ? sorted[0][1] : 1;
                    return (
                      <div key={qId} className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm font-medium text-gray-700 mb-3">{q.questionText}</div>
                        <div className="space-y-2">
                          {sorted.map(([option, count]) => (
                            <div key={option}>
                              <div className="flex justify-between text-xs text-gray-600 mb-0.5">
                                <span className="truncate mr-2">{option}</span>
                                <span className="font-medium whitespace-nowrap">{count}</span>
                              </div>
                              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-indigo-400 rounded-full"
                                  style={{ width: `${(count / maxCount) * 100}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Text / Open-Ended */}
              {texts.length > 0 && (
                <div className="grid grid-cols-1 gap-3">
                  {texts.map(([qId, q]) => (
                    <div key={qId} className="bg-white rounded-lg shadow p-4">
                      <button
                        onClick={() => setExpandedQuestions(prev => ({ ...prev, [qId]: !prev[qId] }))}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="text-sm font-medium text-gray-700">{q.questionText}</div>
                        <div className="flex items-center space-x-2 ml-3 shrink-0">
                          <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-sm font-medium">
                            {q.count} response{q.count !== 1 ? 's' : ''}
                          </span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedQuestions[qId] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      {expandedQuestions[qId] && q.responses && q.responses.length > 0 && (
                        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                          {q.responses.map((resp, i) => (
                            <div key={i} className="border-l-2 border-indigo-200 pl-3 py-1">
                              <p className="text-sm text-gray-700">{resp}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {Object.keys(questionsBySubmissionType).length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400 mb-10">
            No survey response data available{activeTab !== 'all' ? ` for ${activeTab}` : ''}.
          </div>
        )}

        {/* Section 3: Class Comparison (when All tab active) */}
        {activeTab === 'all' && classCodes.length > 1 && (() => {
          const allRatings = Object.entries(questions).filter(([, q]) => q.type === 'rating');
          if (allRatings.length === 0) return null;
          return (
            <section className="mb-10">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Cross-Class Rating Comparison</h2>
              <div className="bg-white rounded-lg shadow p-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 text-gray-600 font-medium">Question</th>
                      {classCodes.map(cc => (
                        <th key={cc} className="text-center py-2 px-3 text-gray-600 font-medium">{cc}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allRatings.map(([qId, q]) => (
                      <tr key={qId} className="border-b border-gray-50">
                        <td className="py-2 pr-4 text-gray-700 max-w-xs truncate">{q.questionText}</td>
                        {classCodes.map(cc => {
                          const classQ = questionAggregates[cc]?.[qId];
                          return (
                            <td key={cc} className="text-center py-2 px-3">
                              {classQ?.avg ? (
                                <span className="font-semibold text-indigo-600">{classQ.avg}</span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })()}
      </main>
    </div>
  );
}

export default FocusGroupAnalytics;
